import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Contract } from '../types';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { AlertCircle, X, Download, ArrowRight, FileText, Info, CheckCircle, Star, Zap, ChevronRight } from 'lucide-react';

interface ProposalGeneratorProps {
  contract: Contract;
  onClose: () => void;
  onSuccess: () => void;
  existingProposal?: boolean;
}

interface ProposalResponse {
  proposal: {
    coverLetter: string;
    technicalSections: Array<{
      title: string;
      content: string;
    }>;
    feedback: string;
    contractAnalysis: string;
    profileAnalysis: string;
    message: string;
  };
}

interface Document {
  id: string;
  file_name: string;
  document_type: string;
  extracted_text: string;
}

interface GenerationStep {
  id: string;
  title: string;
  description: string;
  progress: number;
  status: 'pending' | 'active' | 'complete' | 'error';
}

const GENERATION_STEPS: GenerationStep[] = [
  {
    id: 'contract',
    title: 'Analyzing Contract',
    description: 'Analyzing contract requirements and specifications',
    progress: 25,
    status: 'pending'
  },
  {
    id: 'profile',
    title: 'Analyzing Profile',
    description: 'Analyzing company profile and capabilities',
    progress: 50,
    status: 'pending'
  },
  {
    id: 'proposal',
    title: 'Generating Proposal',
    description: 'Compiling proposal content and sections',
    progress: 75,
    status: 'pending'
  },
  {
    id: 'feedback',
    title: 'Finalizing',
    description: 'Generating feedback and recommendations',
    progress: 100,
    status: 'pending'
  }
];

export function ProposalGenerator({
  contract,
  onClose,
  onSuccess,
  existingProposal = false
}: ProposalGeneratorProps) {
  const { profile } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [proposalContent, setProposalContent] = useState<ProposalResponse | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [showDocumentSelection, setShowDocumentSelection] = useState(true);
  const [currentStep, setCurrentStep] = useState<string>('contract');
  const [currentProgress, setCurrentProgress] = useState(0);
  const [steps, setSteps] = useState<GenerationStep[]>(GENERATION_STEPS);

  useEffect(() => {
    if (profile) {
      loadDocuments();
    }
  }, [profile]);

  const loadDocuments = async () => {
    try {
      if (!profile) return;

      const { data, error } = await supabase
        .from('profile_documents')
        .select('id, file_name, document_type, extracted_text')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleConfirmDocuments = async () => {
    setShowDocumentSelection(false);
    
    try {
      await handleGenerate();
    } catch (error) {
      console.error('Error starting proposal generation:', error);
      setShowDocumentSelection(true);
      toast.error('Failed to start proposal generation');
    }
  };

  const updateStepStatus = (stepId: string, status: GenerationStep['status']) => {
    setSteps(prevSteps => 
      prevSteps.map(step => ({
        ...step,
        status: step.id === stepId ? status : step.status
      }))
    );
  };

  const updateProgress = (message: string) => {
    let stepId: string;
    let progress: number;

    switch (message.toLowerCase()) {
      case 'analyzing contract requirements':
        stepId = 'contract';
        progress = 25;
        break;
      case 'analyzing company profile':
        stepId = 'profile';
        progress = 50;
        break;
      case 'compiling proposal content':
        stepId = 'proposal';
        progress = 75;
        break;
      case 'finalizing proposal':
        stepId = 'feedback';
        progress = 100;
        break;
      default:
        return;
    }

    setCurrentStep(stepId);
    setCurrentProgress(progress);
    updateStepStatus(stepId, 'active');

    // Mark previous steps as complete
    const stepIndex = GENERATION_STEPS.findIndex(s => s.id === stepId);
    if (stepIndex > 0) {
      for (let i = 0; i < stepIndex; i++) {
        updateStepStatus(GENERATION_STEPS[i].id, 'complete');
      }
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setProgress('Validating request data...');
      setError(null);

      if (!profile) {
        throw new Error('Please complete your profile before generating a proposal');
      }

      // Reset steps
      setSteps(GENERATION_STEPS.map(step => ({ ...step, status: 'pending' })));
      setCurrentProgress(0);
      setCurrentStep('contract');

      // Get selected documents' text
      const selectedTexts = documents
        .filter(doc => selectedDocuments.includes(doc.id))
        .map(doc => doc.extracted_text)
        .filter(Boolean);

      // Prepare request data
      const requestData = {
        contract: {
          id: contract.id,
          title: contract.title,
          description: contract.description,
          agency: contract.agency,
          naicsCode: contract.naicsCode,
          dueDate: contract.dueDate,
          fundingAmount: contract.fundingAmount,
          setAsides: contract.setAsides
        },
        profile: {
          id: profile.id,
          company_name: profile.company_name,
          core_competencies: profile.core_competencies || [],
          industries_served: profile.industries_served || [],
          key_technologies: profile.key_technologies || [],
          value_proposition: profile.value_proposition || '',
          duns_number: profile.duns_number || '',
          cage_code: profile.cage_code || '',
          qualified_set_asides: profile.qualified_set_asides || []
        },
        selectedDocumentText: selectedTexts.join('\n\n')
      };

      // Update progress for first step
      updateProgress('Analyzing contract requirements');

      // Call the edge function
      const { data, error: functionError } = await supabase.functions.invoke(
        'generate-proposal',
        {
          body: JSON.stringify(requestData)
        }
      );

      if (functionError) {
        console.error('Function error:', functionError);
        throw new Error(functionError.message || 'Failed to generate proposal');
      }

      if (!data) {
        throw new Error('No response data received');
      }

      // Validate response structure
      if (!isValidProposalResponse(data)) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid proposal structure received');
      }

      // Update progress for final steps
      updateProgress('Finalizing proposal');

      // Generate a placeholder template URL based on contract ID and timestamp
      const templateUrl = `proposal-templates/${contract.id}/${Date.now()}/proposal.pdf`;

      // Store the proposal
      const { error: storageError } = await supabase
        .from('proposal_templates')
        .insert({
          profile_id: profile.id,
          notice_id: contract.id,
          template_name: `Proposal for ${contract.title}`,
          content_json: data.proposal,
          template_url: templateUrl,
          created_at: new Date().toISOString()
        });

      if (storageError) {
        console.error('Storage error:', storageError);
        throw new Error('Failed to save proposal');
      }

      // Mark all steps as complete
      setSteps(prevSteps => 
        prevSteps.map(step => ({
          ...step,
          status: 'complete'
        }))
      );

      setProposalContent(data);
      toast.success('Proposal generated successfully');
      onSuccess();
    } catch (error) {
      console.error('Error generating proposal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate proposal';
      setError(errorMessage);
      toast.error(errorMessage);
      setShowDocumentSelection(true);

      // Mark current step as error
      updateStepStatus(currentStep, 'error');
    } finally {
      setGenerating(false);
      setProgress('');
    }
  };

  function isValidProposalResponse(data: any): data is ProposalResponse {
    return (
      data &&
      typeof data === 'object' &&
      data.proposal &&
      typeof data.proposal === 'object' &&
      typeof data.proposal.coverLetter === 'string' &&
      Array.isArray(data.proposal.technicalSections) &&
      data.proposal.technicalSections.every(section => 
        typeof section === 'object' &&
        typeof section.title === 'string' &&
        typeof section.content === 'string'
      ) &&
      typeof data.proposal.feedback === 'string' &&
      typeof data.proposal.contractAnalysis === 'string' &&
      typeof data.proposal.profileAnalysis === 'string'
    );
  }

  const getCurrentStep = () => steps.find(step => step.id === currentStep);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">
            {existingProposal ? 'Update Proposal' : 'Generate Proposal'}
          </h2>
          <button onClick={onClose} className="text-bw-muted hover:text-bw-muted">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Document Selection Section */}
          {showDocumentSelection && !proposalContent && !error && (
            <div className="mb-8">
              <div className="bg-bw-primary-tint rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-bw-primary mt-0.5 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-bw-primary">
                      Select Supporting Documents
                    </h3>
                    <p className="mt-1 text-sm text-bw-primary/80">
                      Choose up to 3 documents to enhance your proposal. The content from these documents will be used to create a more targeted and comprehensive proposal.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {documents.map((doc) => (
                  <div 
                    key={doc.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      selectedDocuments.includes(doc.id)
                        ? 'border-servet-blue bg-bw-primary-tint'
                        : 'border-bw-border hover:border-servet-blue'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.includes(doc.id)}
                        onChange={(e) => {
                          if (e.target.checked && selectedDocuments.length >= 3) {
                            toast.error('Maximum of 3 documents can be selected');
                            return;
                          }
                          setSelectedDocuments(prev =>
                            e.target.checked
                              ? [...prev, doc.id]
                              : prev.filter(id => id !== doc.id)
                          );
                        }}
                        className="mt-1 h-4 w-4 text-bw-primary border-bw-border rounded focus:ring-bw-primary"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-bw-muted mr-2" />
                          <span className="font-medium text-bw-ink">{doc.file_name}</span>
                          <span className="ml-2 text-sm text-bw-muted">({doc.document_type})</span>
                        </div>
                        {doc.extracted_text && (
                          <p className="mt-2 text-sm text-bw-muted line-clamp-2">
                            {doc.extracted_text}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {documents.length === 0 && (
                  <div className="text-center py-6 bg-bw-surface-subtle rounded-lg">
                    <FileText className="h-12 w-12 text-bw-muted mx-auto mb-3" />
                    <p className="text-bw-muted">No documents available</p>
                    <p className="text-sm text-bw-muted mt-1">
                      Upload documents in your profile to include them in proposals
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-bw-muted hover:text-bw-ink border border-bw-border rounded-md hover:bg-bw-surface-subtle"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDocuments}
                  className="flex items-center px-6 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
                >
                  {selectedDocuments.length > 0 ? (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Confirm Selection & Generate
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-5 w-5 mr-2" />
                      Skip & Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {error ? (
            <div className="text-center py-8">
              <div className="flex items-center justify-center text-urgent mb-4">
                <AlertCircle className="h-8 w-8 mr-2" />
                <span className="text-lg">{error}</span>
              </div>
              <button
                onClick={() => {
                  setError(null);
                  setShowDocumentSelection(true);
                }}
                className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : proposalContent ? (
            <div className="space-y-6">
              <div className="bg-success-bg border border-success-border rounded-lg p-4 mb-4">
                <p className="text-success">
                  {existingProposal
                    ? 'Proposal updated successfully!'
                    : 'Proposal generated successfully!'}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contract Analysis</h3>
                <div className="bg-bw-surface-subtle p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{proposalContent.proposal.contractAnalysis}</p>
                </div>

                <h3 className="text-lg font-semibold">Profile Analysis</h3>
                <div className="bg-bw-surface-subtle p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{proposalContent.proposal.profileAnalysis}</p>
                </div>

                <h3 className="text-lg font-semibold">Cover Letter</h3>
                <div className="bg-bw-surface-subtle p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{proposalContent.proposal.coverLetter}</p>
                </div>

                {proposalContent.proposal.technicalSections.map((section, index) => (
                  <div key={index}>
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                    <div className="bg-bw-surface-subtle p-4 rounded-lg">
                      <p className="whitespace-pre-wrap">{section.content}</p>
                    </div>
                  </div>
                ))}

                <h3 className="text-lg font-semibold">AI Feedback</h3>
                <div className="bg-bw-primary-tint p-4 rounded-lg">
                  <p className="text-bw-primary whitespace-pre-wrap">{proposalContent.proposal.feedback}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-bw-muted hover:text-bw-ink border border-bw-border rounded-md hover:bg-bw-surface-subtle"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    toast.success('Download feature coming soon!');
                  }}
                  className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Proposal
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              {/* Progress Bar and Steps */}
              <div className="max-w-xl mx-auto mb-8">
                <div className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-bw-primary bg-bw-primary-tint">
                        {getCurrentStep()?.title || 'Starting...'}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-bw-primary">
                        {currentProgress}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-bw-primary-tint">
                    <div
                      style={{ width: `${currentProgress}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-bw-primary transition-all duration-500"
                    ></div>
                  </div>
                </div>

                {/* Steps Display */}
                <div className="space-y-4 mt-6">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className={`flex items-center ${
                        currentStep === step.id
                          ? 'text-bw-primary'
                          : step.status === 'complete'
                          ? 'text-success'
                          : step.status === 'error'
                          ? 'text-urgent'
                          : 'text-bw-muted'
                      }`}
                    >
                      {step.status === 'complete' ? (
                        <CheckCircle className="h-5 w-5 mr-3" />
                      ) : currentStep === step.id ? (
                        <Zap className="h-5 w-5 mr-3 animate-pulse" />
                      ) : step.status === 'error' ? (
                        <AlertCircle className="h-5 w-5 mr-3" />
                      ) : (
                        <Star className="h-5 w-5 mr-3" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{step.title}</p>
                        <p className="text-sm opacity-75">{step.description}</p>
                      </div>
                      {currentStep === step.id && (
                        <ChevronRight className="h-5 w-5 animate-pulse" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {selectedDocuments.length > 0 && (
                <p className="mt-2 text-sm text-bw-muted">
                  Including content from {selectedDocuments.length} selected document{selectedDocuments.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}